import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix the default marker icons that Webpack/CRA breaks
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const numberedIcon = (n, color = '#f59e0b') =>
  L.divIcon({
    className: 'route-numbered-marker',
    html: `<div style="background:${color};color:#fff;border-radius:9999px;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-weight:700;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid #fff;">${n}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });

const centerIcon = L.divIcon({
  className: 'route-center-marker',
  html: `<div style="background:#0f766e;color:#fff;border-radius:9999px;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-weight:700;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:3px solid #fff;">C</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

const DriverRouteMap = ({ stops: stopsInput = [], direction = 'pickup', centerCoords = null }) => {
  const stops = useMemo(() => {
    return stopsInput
      .map((c, i) => {
        const coords = direction === 'pickup' ? c.pickup_coords : c.dropoff_coords;
        if (!coords || coords.lat == null || coords.lng == null) return null;
        return {
          id: c.id,
          index: c.stop_order || i + 1,
          name: c.display_name || 'Child',
          address:
            (direction === 'pickup' ? c.pickup_address : c.dropoff_address) || '',
          label: (direction === 'pickup' ? c.pickup_label : c.dropoff_label) || '',
          lat: coords.lat,
          lng: coords.lng,
        };
      })
      .filter(Boolean);
  }, [stopsInput, direction]);

  const bounds = useMemo(() => {
    const pts = stops.map((s) => [s.lat, s.lng]);
    if (centerCoords) pts.push([centerCoords.lat, centerCoords.lng]);
    if (pts.length === 0) return null;
    return L.latLngBounds(pts).pad(0.15);
  }, [stops, centerCoords]);

  if (stops.length === 0) {
    return (
      <div
        className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground bg-slate-50"
        data-testid="driver-map-empty"
      >
        No addresses could be located on the map yet. Ask your admin to double-check the pickup / dropoff addresses.
      </div>
    );
  }

  const polylinePoints = stops.map((s) => [s.lat, s.lng]);

  return (
    <div
      className="rounded-xl overflow-hidden border border-border h-[420px] shadow-sm"
      data-testid="driver-route-map"
    >
      <MapContainer
        bounds={bounds || undefined}
        scrollWheelZoom={true}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Polyline positions={polylinePoints} pathOptions={{ color: '#f59e0b', weight: 4, opacity: 0.7 }} />
        {stops.map((s) => (
          <Marker key={s.id} position={[s.lat, s.lng]} icon={numberedIcon(s.index)}>
            <Popup>
              <div className="text-sm">
                <div className="font-semibold">
                  Stop {s.index}: {s.name}
                </div>
                {s.label && <div className="text-xs text-slate-600 mt-0.5">{s.label}</div>}
                <div className="text-xs text-slate-700 mt-1">{s.address}</div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                    s.address,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-xs underline mt-2 inline-block"
                >
                  Navigate
                </a>
              </div>
            </Popup>
          </Marker>
        ))}
        {centerCoords && (
          <Marker position={[centerCoords.lat, centerCoords.lng]} icon={centerIcon}>
            <Popup>Center</Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};

export default DriverRouteMap;
